import React from 'react';
import isNil from 'lodash/isNil';
import classnames from 'classnames';
import { observer } from 'mobx-react';

import style from './EditEntryData.module.scss';
import '@components/Print/Print_.scss';
import '@components/mixins/commonStyles.scss';

import { translate } from '@chaika/i18n/helper';

import { AsyncSelect, OptionType, Select } from '@components/Select';
import { LeftTitle } from '@components/LeftTitle';
import { EntryPageTestAttributes } from 'chaika-test-attributes';
import { LeftTitleModifiers } from '@components/LeftTitle/LeftTitle';
import { store } from '@store';

export interface EditEntryDataProps {}

interface Props extends EditEntryDataProps {
    disableChangeEntryType: boolean;
    isMobile: boolean;
    selectedEntryTypeId: string;
    informedConsentSigned?: boolean;
    entryTypeOptions: OptionType[];
    reservationId?: string | null;
    reservationOptions: OptionType[];
    entryTypeName: string;
    isEntryViewMode: boolean;
    isSigned: boolean;
    hasEDS: boolean;
    onReservationSelect(params: OptionType): void;
    onEntryTypeSelect(params: OptionType): void;
    loadEntryTypeOptions(query: string): Promise<OptionType<string>[]>;
}

export const EditEntryData = observer(
    ({
        disableChangeEntryType,
        informedConsentSigned,
        isEntryViewMode,
        isSigned,
        hasEDS,
        entryTypeName,
        selectedEntryTypeId,
        entryTypeOptions,
        reservationId,
        reservationOptions,
        onReservationSelect,
        onEntryTypeSelect,
        loadEntryTypeOptions,
        isMobile,
    }: Props) => {
        const {
            entry: { isTranslationMode },
        } = store;
        const tEntryTypeLabel = translate('entryTypeLabel');
        const tLinkToBookingLabel = translate('linkToBookingLabel');
        const tSearchPlaceHolder = translate('searchPlaceHolder');
        const tInformedConsentSignedLabel = translate('informedConsentSignedLabel');
        const tInformedConsentNotSignedLabel = translate('informedConsentNotSignedLabel');
        const tHasEDS = translate('hasEDS');

        return (
            <div className={classnames([style.root, 'hide_on_print'])}>
                {isEntryViewMode ? (
                    <React.Fragment>
                        {!isSigned && <div className={style.entryTypeTitle}>{tEntryTypeLabel}:</div>}
                        <div className={style.entryTypeName}>{entryTypeName}</div>
                        {hasEDS && <div className={classnames(['marker', 'marker__good'])}>{tHasEDS}</div>}
                        <LeftTitle
                            light
                            customTitleWidth={isMobile ? undefined : 52}
                            title={tLinkToBookingLabel}
                        >
                            <div className={style.entryTypeSelect}>
                                <Select
                                    selectedValue={reservationId}
                                    options={reservationOptions}
                                    disabled={isTranslationMode ?? reservationOptions.length <= 1}
                                    onChange={onReservationSelect}
                                    dataTest={EntryPageTestAttributes.EntryHeaderReservationType}
                                />
                            </div>
                        </LeftTitle>
                    </React.Fragment>
                ) : (
                    <>
                        <LeftTitle
                            light
                            modifiers={[LeftTitleModifiers.SmallGap]}
                            customTitleWidth={isMobile ? undefined : 45}
                            title={tEntryTypeLabel}
                        >
                            <div className={style.entryTypeSelect}>
                                <AsyncSelect
                                    disabled={isTranslationMode ?? disableChangeEntryType}
                                    loadOptions={loadEntryTypeOptions}
                                    selectedValue={selectedEntryTypeId}
                                    defaultOptions={[
                                        {
                                            value: null,
                                            label: tSearchPlaceHolder,
                                            isDisabled: true,
                                        },
                                        ...entryTypeOptions,
                                    ]}
                                    onChange={onEntryTypeSelect}
                                    dataTest={EntryPageTestAttributes.EntryHeaderEntryType}
                                />
                            </div>
                        </LeftTitle>
                        {!isNil(informedConsentSigned) && (
                            <div
                                className={classnames([style.consent, informedConsentSigned && style.consent_confirm])}
                            >
                                {informedConsentSigned ? tInformedConsentSignedLabel : tInformedConsentNotSignedLabel}
                            </div>
                        )}
                        <LeftTitle
                            light
                            customTitleWidth={isMobile ? undefined : 52}
                            title={tLinkToBookingLabel}
                        >
                            <div className={style.entryTypeSelect}>
                                <Select
                                    selectedValue={reservationId}
                                    options={reservationOptions}
                                    disabled={isTranslationMode ?? reservationOptions.length <= 1}
                                    onChange={onReservationSelect}
                                    dataTest={EntryPageTestAttributes.EntryHeaderReservationType}
                                />
                            </div>
                        </LeftTitle>
                    </>
                )}
            </div>
        );
    },
);
